import { useState, useEffect, useRef, useCallback } from 'react';

// Types based on API docs
type WebSocketEvent = 'ping' | 'subscribe' | 'join' | 'leave' | 'publish';

interface WebSocketMessage {
    event: string;
    data?: any;
}

interface UseAeraWebSocketProps {
    roomId: string | null;
    token: string | null;
    enabled?: boolean;
    onMessage?: (data: any) => void;
}

interface UseAeraWebSocketReturn {
    isConnected: boolean;
    send: (event: string, data: any) => void;
    on: (requestId: string, callback: (data: any) => void) => void;
    off: (requestId: string, callback: (data: any) => void) => void;
    // processingMessage: string | null;
    // setProcessingMessage: (message: string | null) => void;
}

const WS_URL = process.env.NEXT_PUBLIC_WS_URL || '';

// --- Singleton State ---
let globalWs: WebSocket | null = null;
let globalIsConnected = false;
let globalProcessingMessage: string | null = null;
let lastMessageTime = Date.now();
let reconnectTimeout: NodeJS.Timeout | null = null;
let currentRoomId: string | null = null;
let currentToken: string | null = null;

const onMessageHandlers = new Set<(data: any) => void>();
const statusListeners = new Set<(connected: boolean) => void>();
const processingListeners = new Set<(msg: string | null) => void>();
const requestListenersMap = new Map<string, Set<(data: any) => void>>();

function notifyStatus(connected: boolean) {
    globalIsConnected = connected;
    statusListeners.forEach(l => l(connected));
}

function notifyProcessing(msg: string | null) {
    globalProcessingMessage = msg;
    processingListeners.forEach(l => l(msg));
}

function notifyMessage(message: any) {
    onMessageHandlers.forEach(h => h(message));
}

function connect(roomId: string | null, token: string | null, enabled: boolean) {
    if (!enabled || !roomId || !token) return;

    // Avoid redundant connections if already connected (or connecting) to same room+token
    if (
        globalWs &&
        (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING) &&
        currentRoomId === roomId &&
        currentToken === token
    ) {
        return;
    }

    currentRoomId = roomId;
    currentToken = token;

    // Clean up token if it contains "Bearer "
    const cleanToken = token.trim().startsWith('Bearer ') ? token.trim().substring(7) : token.trim();
    const url = `${WS_URL}?token=${cleanToken}`;

    if (globalWs) {
        globalWs.close();
    }

    const ws = new WebSocket(url);
    globalWs = ws;

    ws.onopen = () => {
        notifyStatus(true);
        lastMessageTime = Date.now();

        if (roomId) {
            ws.send(JSON.stringify({
                event: 'subscribe',
                data: { roomId }
            }));
        }
    };

    ws.onmessage = (event) => {
        lastMessageTime = Date.now();
        try {
            const message = JSON.parse(event.data);
            
            if (message.event === 'ping') {
                ws.send(JSON.stringify({ event: 'pong', data: { time: Date.now() } }));
                return;
            }
            if (message.event === 'pong') return;

            // Request ID listeners
            if (message.data && message.data.request_id) {
                const handlers = requestListenersMap.get(message.data.request_id);
                if (handlers) {
                    handlers.forEach(h => h(message.data));
                }
            }

            // Processing Indicator Logic
            if (message.data) {
                const data = message.data;
                const isEnd = data.end === true || data.status === 'completed' || data.status === 'error';
                const isProcessingStream = message.event === 'stream' && data.request_id === "";

                if (isProcessingStream) {
                    if (!isEnd) {
                        const rawText = data.message || data.text || data.response || "";
                        const msgToDisplay = rawText || "กำลังดำเนินการ...";
                        
                        if (globalProcessingMessage === "กำลังดำเนินการ...") {
                            notifyProcessing(rawText || globalProcessingMessage);
                        } else {
                            notifyProcessing(globalProcessingMessage ? globalProcessingMessage + rawText : msgToDisplay);
                        }
                    } else {
                        notifyProcessing(null);
                    }
                }
            }

            notifyMessage(message);
        } catch (e) {
            console.error('Error parsing WebSocket message:', e);
        }
    };

    ws.onclose = () => {
        // Only handle if this ws is still the active singleton connection
        if (globalWs !== ws) return;
        notifyStatus(false);
        globalWs = null;

        if (reconnectTimeout) clearTimeout(reconnectTimeout);
        reconnectTimeout = setTimeout(() => {
            connect(currentRoomId, currentToken, enabled);
        }, 3000);
    };

    ws.onerror = (error) => {
        if (globalWs !== ws) return;
        console.warn('WebSocket Error:', error);
    };
}

// Global Reconnect / Health Check Interval
setInterval(() => {
    if (!globalWs || globalWs.readyState === WebSocket.CLOSED) {
        if (currentRoomId && currentToken) connect(currentRoomId, currentToken, true);
    } else if (globalWs.readyState === WebSocket.OPEN) {
        if (Date.now() - lastMessageTime > 35000) {
            globalWs.close();
        } else {
            try {
                globalWs.send(JSON.stringify({ event: 'ping', data: { time: Date.now() } }));
            } catch (e) {}
        }
    }
}, 10000);


export const useAeraWebSocket = ({
    roomId,
    token,
    enabled = true,
    onMessage,
}: UseAeraWebSocketProps): UseAeraWebSocketReturn => {
    const [localConnected, setLocalConnected] = useState(globalIsConnected);
    const [localProcessing, setLocalProcessing] = useState(globalProcessingMessage);

    // Stable ref to always call the latest onMessage without re-registering the handler
    const onMessageRef = useRef(onMessage);
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        // Create a stable wrapper that delegates to the current ref
        const stableHandler = (msg: any) => {
            if (onMessageRef.current) onMessageRef.current(msg);
        };

        onMessageHandlers.add(stableHandler);
        statusListeners.add(setLocalConnected);
        processingListeners.add(setLocalProcessing);

        if (enabled) {
            connect(roomId, token, enabled);
        }

        return () => {
            onMessageHandlers.delete(stableHandler);
            statusListeners.delete(setLocalConnected);
            processingListeners.delete(setLocalProcessing);
        };
    }, [roomId, token, enabled]); // onMessage removed from deps — handled via ref

    const send = useCallback((event: string, data: any) => {
        if (globalWs && globalWs.readyState === WebSocket.OPEN) {
            globalWs.send(JSON.stringify({ event, data }));
        }
    }, []);

    const on = useCallback((requestId: string, callback: (data: any) => void) => {
        if (!requestListenersMap.has(requestId)) {
            requestListenersMap.set(requestId, new Set());
        }
        requestListenersMap.get(requestId)?.add(callback);
    }, []);

    const off = useCallback((requestId: string, callback: (data: any) => void) => {
        const handlers = requestListenersMap.get(requestId);
        if (handlers) {
            handlers.delete(callback);
            if (handlers.size === 0) {
                requestListenersMap.delete(requestId);
            }
        }
    }, []);

    return { 
        isConnected: localConnected, 
        send, 
        on, 
        off, 
        // processingMessage: localProcessing, 
        // setProcessingMessage: notifyProcessing 
    };
};
